import React, { useEffect, useMemo, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { message } from "antd";
import {
  ShareAltOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import "./ShareMenu.sass";

const messages = defineMessages({
  shareSystem: {
    id: "share.system",
    defaultMessage: "Share via system panel",
  },
  shareCopyImage: {
    id: "share.copyImage",
    defaultMessage: "Copy image to clipboard",
  },
  shareCopied: {
    id: "share.copied",
    defaultMessage: "Image copied!",
  },
  shareError: {
    id: "share.error",
    defaultMessage: "Unable to generate share image",
  },
});

function isMobileDevice(): boolean {
  // Only enable Web Share on actual mobile devices — desktop Chrome's
  // navigator.share() implementation is unreliable: the dialog may not
  // appear, and the promise may hang, leaving sharingRef stuck.
  if (typeof navigator === "undefined") return false;
  if (typeof window === "undefined") return false;
  return (
    /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent) ||
    window.matchMedia("(pointer: coarse) and (max-width: 1024px)").matches
  );
}

interface ShareMenuProps {
  blob: Blob | null;
  verseText: string;
  onClose: () => void;
  sharingRef: React.MutableRefObject<boolean>;
}

const ShareMenu: React.FC<ShareMenuProps> = ({ blob, verseText, onClose, sharingRef }) => {
  const intl = useIntl();
  const [messageApi, contextHolder] = message.useMessage();
  const shareBtnRef = useRef<HTMLButtonElement>(null);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const [sharing, setSharing] = useState(false);

  const blobRef = useRef(blob);
  blobRef.current = blob;
  const verseTextRef = useRef(verseText);
  verseTextRef.current = verseText;

  const showSystemShare = useMemo(() => {
    return typeof navigator !== "undefined" && !!navigator.share && isMobileDevice();
  }, []);

  useEffect(() => {
    const shareEl = shareBtnRef.current;
    const copyEl = copyBtnRef.current;

    const onSystemShare = () => {
      const b = blobRef.current;
      if (!b || sharingRef.current || sharing) return;

      sharingRef.current = true;
      setSharing(true);

      const file = new File([b], "luminaverse.png", { type: "image/png" });
      const shareData: ShareData = { title: "LuminaVerse", text: verseTextRef.current };

      const doShare = (): Promise<void> => {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          return navigator.share({ ...shareData, files: [file] });
        }
        return navigator.share(shareData);
      };

      // Safety timeout: if navigator.share promise hangs (desktop Chrome),
      // reset state after 5s so the button isn't permanently blocked.
      const timeout = setTimeout(() => {
        sharingRef.current = false;
        setSharing(false);
        onClose();
      }, 5000);

      doShare()
        .catch((err: DOMException) => {
          if (err.name !== "AbortError") {
            console.error("System share failed:", err);
            messageApi.error(intl.formatMessage(messages.shareError));
          }
        })
        .finally(() => {
          clearTimeout(timeout);
          sharingRef.current = false;
          setSharing(false);
          onClose();
        });
    };

    const onCopyImage = () => {
      const b = blobRef.current;
      if (!b || sharing) return;

      setSharing(true);

      navigator.clipboard.write([new ClipboardItem({ "image/png": b })])
        .then(() => {
          messageApi.success(intl.formatMessage(messages.shareCopied));
          onClose();
        })
        .catch((err: unknown) => {
          console.error("Clipboard copy failed:", err);
          messageApi.error(intl.formatMessage(messages.shareError));
        })
        .finally(() => {
          setSharing(false);
        });
    };

    shareEl?.addEventListener("click", onSystemShare);
    copyEl?.addEventListener("click", onCopyImage);

    return () => {
      shareEl?.removeEventListener("click", onSystemShare);
      copyEl?.removeEventListener("click", onCopyImage);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="share-menu">
      {contextHolder}
      {showSystemShare && (
        <button
          ref={shareBtnRef}
          className="share-menu__option"
          disabled={!blob || sharing}
        >
          <span className="share-menu__icon">
            <ShareAltOutlined />
          </span>
          <span className="share-menu__label">
            {intl.formatMessage(messages.shareSystem)}
          </span>
        </button>
      )}
      <button
        ref={copyBtnRef}
        className="share-menu__option"
        disabled={!blob || sharing}
      >
        <span className="share-menu__icon">
          <CopyOutlined />
        </span>
        <span className="share-menu__label">
          {intl.formatMessage(messages.shareCopyImage)}
        </span>
      </button>
    </div>
  );
};

export default ShareMenu;
